// The fake api's venue commerce, past the console gate in `fake-server.ts`: the F&B menu (spec/13 §6.1,
// F-FNB-01/-02/-03), the venue's sales (spec/13 §4–§5, spec/06 §8–§10, F-POS-09/-01/-12/-13/-15) and
// the kitchen display (spec/13 §6.2, F-FNB-04/-06) — enough for Sell, Orders and its order sheet,
// Kitchen and Menu to render in a browser test with realistic data.
//
//   GET   /v1/organizations/{o}/product-categories                     listMenuCategories   products:read
//   GET   /v1/organizations/{o}/products                               listMenuProducts     products:read
//   PATCH /v1/organizations/{o}/products/{id}/availability             setProductAvailability  products:availability
//   GET   …/venues/{v}/menu                                            getVenueMenu         products:read
//   PATCH …/venues/{v}/menu/products/{id}/availability                 setVenueProductAvailability  products:availability
//   GET   …/venues/{v}/sales                                           listSales            orders:read
//   POST  …/venues/{v}/sales                                           createSale           orders:create
//   POST  …/venues/{v}/sales/quote                                     quoteSale            orders:create
//   GET   …/venues/{v}/sales/{id}                                      getSale              orders:read
//   POST  …/venues/{v}/sales/{id}/payments                             takeSalePayment      orders:create
//   POST  …/venues/{v}/sales/{id}/on-account                           takeSaleOnAccount    orders:on_account
//   POST  …/venues/{v}/sales/{id}/cancel                               cancelSale           orders:void
//   POST  …/venues/{v}/sales/{id}/discount-code                        applyDiscountCode    orders:create
//   GET   …/venues/{v}/sales/{id}/document                             getSaleDocument      orders:read
//   GET   …/venues/{v}/kitchen/tickets                                 listKitchenTickets   kitchen:read
//   POST  …/venues/{v}/kitchen/tickets/{id}/state                      advanceKitchenTicket kitchen:update
//   GET   …/venues/{v}/food-orders/{id}                                getFoodOrder         orders:read
//   POST  …/venues/{v}/food-orders/{id}/cancel                         cancelFoodOrder      orders:void
//
// A food order is read back from its kitchen tickets: every item a line at ₹120, untaxed. Order `6a5`
// (the bar's ready ticket) is settled, so cancelling it is the contract's `409`. Ticket `k08` is a **sale's**
// (F-FNB-04): the open sale `5a1`, number #0421, accepted and not paid — Cancel order opens the sale.
//
// The venue trades as a `regular` GST registrant with tax-inclusive prices (spec/06 §10.3), so every
// line carries CGST + SGST that add back to its price. The pricing is `@clavian/domain`'s own
// `priceFoodOrderLine` — the engine the server prices a sale with — so a line's paisa here is the
// paisa the server would compute, and a sale's totals are derived from its lines, tenders, discounts
// and refunds exactly as `toSaleDto` derives them (spec/06 §8.2, §2.4 invariant 5). Money is integer
// minor units of INR throughout; times are RFC 3339 UTC around 14:00Z (19:30 at the Asia/Kolkata venue).
//
// A `wallet` tender draws the sale player's wallet through `helpers.wallet` (the players fake's), and
// on account charges their account through `helpers.credit` (the credit fake's); with no hook wired,
// the wallet answers `wallet_disabled` and on account `credit_disabled`, as the server does when off.
//
// Bodies are checked with the contract's own schemas. A discount code is priced by `helpers.codes` (the
// promotions fake's). A quote (F-POS-03) prices the cart as `createSale` does and adds the automatic
// promotions from `helpers.promotions` at the fixtures' now; with none wired or none matching, its total
// is the gross. Placing a sale does not apply promotions here (the fake's sales carry none). A refund (F-POS-08) settles
// at once — no approval threshold here — to a counter tender, or to the player's wallet (2026-10-02) through
// `helpers.wallet.creditRefund`, attaching a named player to a sale with none. Other mutations (receipt send, kitchen
// print, menu creation) are not served: `handle` answers `undefined` and the caller's fallback applies.

import type {
  FoodOrder,
  KitchenTicket,
  KitchenTicketState,
  MenuCategory,
  MenuProduct,
  Money,
  Problem,
  Sale,
  SaleDiscount,
  SaleDocument,
  SaleLine,
  SalePayment,
  SaleRefund,
  SaleQuote,
  SaleStatus,
} from '@clavian/api-client';
import {
  AdvanceKitchenTicketSchema,
  ApplyDiscountCodeSchema,
  CreateSaleSchema,
  FoodPreparationAreaSchema,
  KitchenTicketStateSchema,
  QuoteSaleSchema,
  RefundSaleSchema,
  SetProductAvailabilitySchema,
  TakeSaleOnAccountSchema,
  TakeSalePaymentSchema,
  type z,
} from '@clavian/contracts';
import { priceFoodOrderLine, type FoodTaxTreatment } from '@clavian/domain';

import type { CartLineGross, CartPromotions, CodePrice } from './fake-org-promotions';
import type { FakeResponse } from './fake-server';

// Re-declared rather than imported, so this file and `fake-server.ts` do not import each other.
const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
const CURRENCY = 'INR';
/** The fixtures' "now": 14:00 UTC, 19:30 at the venue — the evening rush. */
const NOW = '2026-09-26T14:00:00.000Z';

/** PC-01, the one station `fake-server.ts` seeds (its `STATION_ID`), so the Kitchen names it. */
export const COMMERCE_STATION_PC01 = 'stn_01k5znw4hmfay8vvr14d2pf3cc';
/** Stations the fake's station list does not carry: the Kitchen labels them "Station". */
const STATION_PC04 = 'stn_01k5znw4hmfay8vvr14d2pf3c4';
const STATION_PC07 = 'stn_01k5znw4hmfay8vvr14d2pf3c7';

/** The GSTIN the venue invoices under (Karnataka, state code 29) — spec/06 §10.2. */
const GSTIN = '29AAHCP4821K1Z6';
const FINANCIAL_YEAR = '2026-27';

/** A `regular` venue's treatment with inclusive prices, intra-state (CGST + SGST) — spec/06 §10.3. */
const TREATMENT: FoodTaxTreatment = { charges: true, pricing: 'inclusive', place: 'intra_state' };

/** Restaurant service GST (5%) for food and drink, 18% for play time (spec/06 §10.1). */
const FNB_RATE_BPS = 500;
const TIME_RATE_BPS = 1800;

export interface CommerceHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  /** The console gate: a refusal (401, or 403 second factor) or `undefined` to go on. */
  readonly gate: () => FakeResponse | undefined;
  /**
   * What the server allows — the route's `x-clavian-permission`. Optional: when it is left out every
   * route answers, as if the member held all of the permissions this file names.
   */
  readonly can?: (permission: string) => boolean;
  /** The player's wallet (fake-venue-players.ts): draws `amount` minor units, bonus first, or refuses. */
  readonly wallet?: {
    readonly spend: (playerId: string, amount: number, description: string) => 'ok' | 'insufficient';
    /** Credit wallet cash with a sale refunded to the wallet; `newMoney` (not from the wallet) is held to its maximum. */
    readonly creditRefund?: (playerId: string, amount: number, newMoney: number, description: string) => 'ok' | 'over-balance';
  };
  /** The player's account (fake-venue-credit.ts): charges `amount` minor units, or refuses. */
  readonly credit?: { readonly charge: (playerId: string, amount: number) => 'ok' | 'disabled' | 'over-limit' };
  /** The organisation's discount codes (fake-org-promotions.ts): a code's discount on a cart, reserving a use. */
  readonly codes?: { readonly priceCode: (code: string, grossMinor: number) => CodePrice };
  /** The organisation's automatic promotions (fake-org-promotions.ts): what they take off a cart, for a quote. */
  readonly promotions?: {
    readonly forCart: (cart: { readonly lines: readonly CartLineGross[]; readonly channel: Sale['channel']; readonly venueId: string; readonly at: string }) => CartPromotions;
  };
  /**
   * The pass catalogue (fake-venue-ops.ts): a pass line's name and price from the product's live version, the
   * reason the counter cannot sell it, or `undefined` for no such product (F-POS-01). Absent, a pass line is 404.
   */
  readonly passes?: { readonly priceForCounter: (passProductId: string) => CounterPassPrice | undefined };
  /**
   * Guest passes (fake-venue-guests.ts, F-SES-07): whether the venue serves guests, a guest minted for each guest pass
   * line a sale places, and its code made to work when the sale is paid. Absent, a guest pass line is `guests_off`.
   */
  readonly guests?: {
    readonly enabled: () => boolean;
    readonly mint: (saleId: string, line: { readonly name: string; readonly price: Money; readonly seconds: number; readonly productId: string }) => unknown;
    readonly activate: (saleId: string) => void;
  };
}

/** How the passes fake prices a counter pass line: its name and price, or why it is refused. */
export type CounterPassPrice = { readonly name: string; readonly price: Money } | { readonly refused: string };

/** A TypeID in the fixtures' style: `{prefix}_01k5znw4hmfay8vvr14d2pf{tail}`, the tail three characters. */
const id = (prefix: string, tail: string): string => `${prefix}_01k5znw4hmfay8vvr14d2pf${tail}`;
/** A TypeID for a row a mutation creates: the same stem, numbered (`…d2` and five decimal digits). */
const numberedId = (prefix: string, serial: number): string => `${prefix}_01k5znw4hmfay8vvr14d2${String(serial).padStart(5, '0')}`;
const inr = (amount: number): Money => ({ amount, currency: CURRENCY });
/** An instant `minutes` before the fixtures' now. */
const ago = (minutes: number): string => new Date(Date.parse(NOW) - minutes * 60_000).toISOString();
const sum = (amounts: readonly Money[]): Money => inr(amounts.reduce((total, money) => total + money.amount, 0));

// ---------------------------------------------------------------------------------------------
// The menu (spec/13 §6.1): four categories, fourteen products, one sold out, one outside its schedule
// ---------------------------------------------------------------------------------------------

export const COMMERCE_CATEGORIES: readonly MenuCategory[] = [
  { id: id('mct', 'ca1'), name: 'Snacks', description: 'Quick bites between matches.', sortOrder: 0, visible: true, availabilityWindows: [], inSchedule: true },
  { id: id('mct', 'ca2'), name: 'Meals', description: null, sortOrder: 1, visible: true, availabilityWindows: [], inSchedule: true },
  { id: id('mct', 'ca3'), name: 'Drinks', description: 'Hot, cold and fizzy.', sortOrder: 2, visible: true, availabilityWindows: [], inSchedule: true },
  { id: id('mct', 'ca4'), name: 'Desserts', description: null, sortOrder: 3, visible: true, availabilityWindows: [], inSchedule: true },
];
const [SNACKS, MEALS, DRINKS, DESSERTS] = COMMERCE_CATEGORIES.map((category) => category.id) as [string, string, string, string];

let productOrder = 0;
function product(tail: string, categoryId: string, name: string, price: number, extra: Partial<MenuProduct> = {}): MenuProduct {
  productOrder += 1;
  return {
    id: id('prd', tail),
    categoryId,
    name,
    description: null,
    price: inr(price),
    salePrice: null,
    taxRateBps: FNB_RATE_BPS,
    taxCategoryId: null,
    dietMarker: 'veg',
    allergens: [],
    preparationArea: 'kitchen',
    preparationSeconds: 600,
    itemCategory: 'fnb',
    barcode: null,
    available: true,
    visible: true,
    membersOnly: false,
    channels: ['counter', 'kiosk', 'app'],
    returnable: true,
    availabilityWindows: [],
    inSchedule: true,
    sortOrder: productOrder,
    variants: [],
    modifierGroups: [],
    image: null,
    ...extra,
  };
}

/** The organisation's products, by sort order. Butter chicken is sold out (F-FNB-03). */
export const COMMERCE_PRODUCTS: readonly MenuProduct[] = [
  // Masala fries has a picture (F-FNB-01); the rest show their tinted initial.
  product('p01', SNACKS, 'Masala fries', 12000, {
    description: 'Crinkle-cut fries tossed in peri-peri masala.',
    preparationSeconds: 420,
    image: { fileId: id('fil', 'p01'), url: 'https://cdn.test/masala-fries.webp', variants: [] },
  }),
  product('p02', SNACKS, 'Chicken nuggets (6 pc)', 18000, { dietMarker: 'non_veg', allergens: ['gluten'], preparationSeconds: 480 }),
  product('p03', SNACKS, 'Paneer tikka sandwich', 16000, { allergens: ['gluten', 'dairy'] }),
  product('p04', SNACKS, 'Salted nachos', 9000, { description: 'Packaged, 150 g.', preparationArea: 'none', preparationSeconds: null }),
  product('p05', MEALS, 'Chicken biryani', 28000, {
    dietMarker: 'non_veg',
    allergens: ['dairy'],
    preparationSeconds: 900,
    modifierGroups: [
      {
        id: id('pmg', 'g01'),
        name: 'Add-ons',
        required: false,
        minChoices: 0,
        maxChoices: 2,
        sortOrder: 0,
        modifiers: [
          { id: id('pmo', 'm01'), name: 'Raita', price: inr(3000), available: true, sortOrder: 0 },
          { id: id('pmo', 'm02'), name: 'Boiled egg', price: inr(2000), available: true, sortOrder: 1 },
        ],
      },
    ],
  }),
  product('p06', MEALS, 'Veg hakka noodles', 22000, { allergens: ['gluten', 'soy'] }),
  product('p07', MEALS, 'Egg fried rice', 20000, { dietMarker: 'egg', allergens: ['egg', 'soy'] }),
  product('p08', MEALS, 'Butter chicken with rice', 32000, { dietMarker: 'non_veg', allergens: ['dairy'], preparationSeconds: 1200, available: false }),
  product('p09', DRINKS, 'Cold coffee', 11000, {
    allergens: ['dairy'],
    preparationArea: 'bar',
    preparationSeconds: 180,
    variants: [
      { id: id('pvr', 'v01'), name: 'Regular', price: inr(11000), sortOrder: 0 },
      { id: id('pvr', 'v02'), name: 'Large', price: inr(14000), sortOrder: 1 },
    ],
  }),
  product('p10', DRINKS, 'Masala chai', 4000, { allergens: ['dairy'], preparationArea: 'bar', preparationSeconds: 240 }),
  product('p11', DRINKS, 'Fresh lime soda', 8000, { dietMarker: 'vegan', preparationArea: 'bar', preparationSeconds: 120 }),
  product('p12', DRINKS, 'Cola (330 ml can)', 6000, { dietMarker: 'vegan', preparationArea: 'none', preparationSeconds: null }),
  product('p13', DESSERTS, 'Chocolate brownie', 13000, { dietMarker: 'egg', allergens: ['gluten', 'dairy', 'egg'], preparationSeconds: 120 }),
  // A lunchtime dessert (F-FNB-02): sold 12:00–15:00 in the venue's time, so outside it at the fixtures' 19:30 — listed, not orderable.
  product('p14', DESSERTS, 'Gulab jamun (2 pc)', 9000, {
    allergens: ['dairy'],
    preparationArea: 'none',
    preparationSeconds: null,
    availabilityWindows: [{ days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'], start: '12:00', end: '15:00' }],
    inSchedule: false,
  }),
];

function productNamed(name: string): MenuProduct {
  const found = COMMERCE_PRODUCTS.find((candidate) => candidate.name === name);
  if (found === undefined) throw new Error(`No fixture product "${name}"`);
  return found;
}

// ---------------------------------------------------------------------------------------------
// Sales (spec/06 §8): lines priced by the domain engine, totals derived as the server derives them
// ---------------------------------------------------------------------------------------------

interface LineSpec {
  readonly tail: string;
  readonly quantity: number;
  /** A menu product (a `food` line priced from the menu), or a `time`/`other` line's own terms. */
  readonly product?: MenuProduct;
  readonly kind?: SaleLine['kind'];
  readonly name?: string;
  readonly unitPrice?: number;
  readonly taxRateBps?: number;
  readonly modifiers?: readonly { readonly name: string; readonly price: number }[];
}

/** One sale line, priced with its GST components (spec/06 §8.1, §10.1). `total` is what the player pays. */
function priceLine(spec: LineSpec): SaleLine {
  const unitPrice = spec.product?.price ?? inr(spec.unitPrice ?? 0);
  const taxRateBps = spec.product?.taxRateBps ?? spec.taxRateBps ?? 0;
  const modifiers = (spec.modifiers ?? []).map((modifier) => ({ name: modifier.name, price: inr(modifier.price) }));
  const priced = priceFoodOrderLine({ unitPrice, modifierPrices: modifiers.map((modifier) => modifier.price), quantity: spec.quantity, taxRateBps }, TREATMENT);
  return {
    id: id('sln', spec.tail),
    kind: spec.kind ?? (spec.product === undefined ? 'other' : spec.product.itemCategory === 'retail' ? 'retail' : 'food'),
    productId: spec.product?.id ?? null,
    variantId: null,
    passProductId: null,
    name: spec.product?.name ?? spec.name ?? 'Item',
    quantity: spec.quantity,
    unitPrice,
    modifiers,
    taxRateBps,
    taxable: priced.taxable,
    tax: priced.tax,
    total: priced.gross,
    // Codes and manual discounts are not allocated to lines; only automatic promotions are (F-POS-03).
    discount: inr(0),
    taxComponents: priced.components.map((component) => ({ component: component.component, rateBps: component.rateBps, amount: component.amount })),
    taxCategoryId: null,
    hsnSac: null,
  };
}

/** What a sale is before its derived fields: what was sold, taken, taken off and given back. */
interface SaleRecord {
  readonly id: string;
  readonly channel: Sale['channel'];
  /** The player the sale is for; absent (anonymous) on the seeded sales. */
  readonly playerId?: string | null;
  readonly stationId: string | null;
  readonly note: string | null;
  readonly lines: readonly SaleLine[];
  readonly payments: readonly SalePayment[];
  readonly discounts: readonly SaleDiscount[];
  readonly refunds: readonly SaleRefund[];
  readonly placedAt: string;
  readonly cancelledAt: string | null;
  /** The order's number for the business day (F-FNB-04); absent reads as not yet allocated. */
  readonly orderNumber?: number | null;
  /** A B2B buyer (F-CMP-07); absent reads as a B2C sale. */
  readonly buyer?: Sale['buyer'];
  /** "Scan & pay (UPI)" at the PC (F-PAY-02): the reference the player's UPI note carries; absent for every other sale. */
  readonly scanToPayReference?: string;
  /** The one order a console session placed when it ended (ADR-0034), and that session. */
  readonly origin?: 'console_session';
  readonly sessionId?: string;
  /** The venue's "send a cash overpayment's change to the wallet" capability (F-POS-12); absent reads as not available. */
  readonly changeToWalletAvailable?: boolean;
}

/**
 * The sale as the api returns it: every amount and the status derived from the record (spec/06 §8.2),
 * as `toSaleDto` derives them — `total = subtotal + tax − discount`, `paid` and `refunded` from the
 * succeeded tenders and refunds, `outstanding = max(0, total − paid)`.
 */
function toSale(record: SaleRecord): Sale {
  const subtotal = sum(record.lines.map((line) => line.taxable));
  const tax = sum(record.lines.map((line) => line.tax));
  const discount = sum(record.discounts.map((one) => one.amount));
  const total = inr(subtotal.amount + tax.amount - discount.amount);
  const paid = sum(record.payments.map((payment) => payment.amount));
  const refunded = sum(record.refunds.filter((refund) => refund.status === 'succeeded').map((refund) => refund.amount));
  const outstanding = inr(Math.max(0, total.amount - paid.amount));
  let status: SaleStatus;
  if (record.cancelledAt !== null) status = 'cancelled';
  else if (paid.amount === 0) status = 'placed';
  else if (paid.amount < total.amount) status = 'partially_paid';
  else if (refunded.amount === 0) status = 'paid';
  else status = refunded.amount >= paid.amount ? 'refunded' : 'partially_refunded';
  const settled = paid.amount >= total.amount && paid.amount > 0;
  return {
    id: record.id,
    venueId: VENUE_ID,
    orderNumber: record.orderNumber ?? null,
    channel: record.channel,
    status,
    taxMode: 'regular',
    buyer: record.buyer ?? { name: null, gstin: null, placeOfSupply: 'intra_state' },
    playerId: record.playerId ?? null,
    stationId: record.stationId,
    sessionId: record.sessionId ?? null,
    note: record.note,
    ...(record.scanToPayReference === undefined ? {} : { scanToPayReference: record.scanToPayReference }),
    ...(record.origin === undefined ? {} : { origin: record.origin }),
    ...(record.changeToWalletAvailable === undefined ? {} : { changeToWalletAvailable: record.changeToWalletAvailable }),
    ...(record.changeToWalletAvailable === false ? { changeToWalletUnavailable: 'wallet_off' as const } : {}),
    lines: [...record.lines],
    payments: [...record.payments],
    discounts: [...record.discounts],
    promotions: [],
    refunds: [...record.refunds],
    subtotal,
    tax,
    discount,
    total,
    paid,
    refunded,
    outstanding,
    placedAt: record.placedAt,
    paidAt: settled ? (record.payments.at(-1)?.createdAt ?? null) : null,
    cancelledAt: record.cancelledAt,
  };
}

function cash(tail: string, amount: number, tendered: number, minutes: number): SalePayment {
  return { id: id('spy', tail), tender: 'cash', amount: inr(amount), tendered: inr(tendered), change: inr(tendered - amount), reference: null, status: 'succeeded', createdAt: ago(minutes) };
}

function upi(tail: string, amount: number, reference: string | null, minutes: number): SalePayment {
  return { id: id('spy', tail), tender: 'counter_upi', amount: inr(amount), tendered: null, change: null, reference, status: 'succeeded', createdAt: ago(minutes) };
}

/** The lines of a record, priced, with the total the player pays — so a fixture tender can match it. */
function gross(lines: readonly SaleLine[], discount = 0): number {
  return lines.reduce((total, line) => total + line.total.amount, 0) - discount;
}

function seedSales(): SaleRecord[] {
  const base = { channel: 'counter' as const, stationId: null, note: null, payments: [], discounts: [], refunds: [], cancelledAt: null };

  // 1. Open: placed at PC-01 a few minutes ago, nothing taken yet.
  const s1Lines = [priceLine({ tail: 'a11', product: productNamed('Masala fries'), quantity: 2 }), priceLine({ tail: 'a12', product: productNamed('Cold coffee'), quantity: 1 })];

  // 2. Part paid: a biryani with raita, a chai and an hour of play; ₹200 cash on account of it.
  const s2Lines = [
    priceLine({ tail: 'a21', product: productNamed('Chicken biryani'), quantity: 1, modifiers: [{ name: 'Raita', price: 3000 }] }),
    priceLine({ tail: 'a22', product: productNamed('Masala chai'), quantity: 1 }),
    priceLine({ tail: 'a23', kind: 'time', name: '1 hour · Standard PC', unitPrice: 10000, taxRateBps: TIME_RATE_BPS, quantity: 1 }),
  ];

  // 3. Paid in cash with a ₹50 code off: ₹500 handed over, change due.
  const s3Lines = [priceLine({ tail: 'a31', product: productNamed('Veg hakka noodles'), quantity: 1 }), priceLine({ tail: 'a32', product: productNamed('Fresh lime soda'), quantity: 2 })];
  const s3Discount = 5000;

  // 4. Paid by counter UPI; a refund of the brownie waits for a second member's approval.
  const s4Lines = [priceLine({ tail: 'a41', product: productNamed('Masala chai'), quantity: 3 }), priceLine({ tail: 'a42', product: productNamed('Chocolate brownie'), quantity: 1 })];

  // 5. Split tender: two hours on a Premium PC and two colas, part cash and part UPI.
  const s5Lines = [
    priceLine({ tail: 'a51', kind: 'time', name: '2 hours · Premium PC', unitPrice: 36000, taxRateBps: TIME_RATE_BPS, quantity: 1 }),
    priceLine({ tail: 'a52', product: productNamed('Cola (330 ml can)'), quantity: 2 }),
  ];

  // 6. Part refunded: one portion of nuggets returned to UPI.
  const s6Lines = [priceLine({ tail: 'a61', product: productNamed('Chicken nuggets (6 pc)'), quantity: 2 }), priceLine({ tail: 'a62', product: productNamed('Cold coffee'), quantity: 1 })];

  // 7. Refunded in full, in cash.
  const s7Lines = [priceLine({ tail: 'a71', product: productNamed('Egg fried rice'), quantity: 1 })];

  // 8. Cancelled from the kiosk before any payment.
  const s8Lines = [priceLine({ tail: 'a81', product: productNamed('Paneer tikka sandwich'), quantity: 1 })];

  const nuggetUnit = (s6Lines[0] as SaleLine).total.amount / 2;

  // Newest first, as `listSales` returns them.
  return [
    { ...base, id: id('sal', '5a1'), orderNumber: 421, stationId: COMMERCE_STATION_PC01, lines: s1Lines, placedAt: ago(4) },
    { ...base, id: id('sal', '5a2'), stationId: STATION_PC04, note: 'Biryani to PC-04 with the chai.', lines: s2Lines, payments: [cash('b21', 20000, 20000, 16)], placedAt: ago(18) },
    {
      ...base,
      id: id('sal', '5a3'),
      lines: s3Lines,
      discounts: [{ kind: 'code', code: 'WEEKEND50', type: 'amount', reason: null, amount: inr(s3Discount) }],
      payments: [cash('b31', gross(s3Lines, s3Discount), 50000, 33)],
      placedAt: ago(35),
    },
    {
      ...base,
      id: id('sal', '5a4'),
      lines: s4Lines,
      payments: [upi('b41', gross(s4Lines), '627140918832', 51)],
      refunds: [
        {
          id: id('srf', 'r41'),
          status: 'pending_approval',
          tender: 'counter_upi',
          amount: (s4Lines[1] as SaleLine).total,
          reason: 'Brownie was served stale.',
          lines: [{ saleLineId: (s4Lines[1] as SaleLine).id, quantity: 1 }],
          requestedAt: ago(20),
          settledAt: null,
        },
      ],
      placedAt: ago(52),
    },
    {
      ...base,
      id: id('sal', '5a5'),
      stationId: STATION_PC07,
      lines: s5Lines,
      payments: [cash('b51', 20000, 20000, 74), upi('b52', gross(s5Lines) - 20000, '627140771205', 73)],
      placedAt: ago(75),
    },
    {
      ...base,
      id: id('sal', '5a6'),
      lines: s6Lines,
      payments: [upi('b61', gross(s6Lines), '627139904417', 94)],
      refunds: [
        {
          id: id('srf', 'r61'),
          status: 'succeeded',
          tender: 'counter_upi',
          amount: inr(nuggetUnit),
          reason: 'One portion of nuggets came out cold.',
          lines: [{ saleLineId: (s6Lines[0] as SaleLine).id, quantity: 1 }],
          requestedAt: ago(80),
          settledAt: ago(80),
        },
      ],
      placedAt: ago(95),
    },
    {
      ...base,
      id: id('sal', '5a7'),
      lines: s7Lines,
      payments: [cash('b71', gross(s7Lines), 20000, 119)],
      refunds: [
        {
          id: id('srf', 'r71'),
          status: 'succeeded',
          tender: 'cash',
          amount: inr(gross(s7Lines)),
          reason: 'Wrong order: the player asked for veg.',
          lines: [{ saleLineId: (s7Lines[0] as SaleLine).id, quantity: 1 }],
          requestedAt: ago(110),
          settledAt: ago(110),
        },
      ],
      placedAt: ago(120),
    },
    { ...base, id: id('sal', '5a8'), channel: 'kiosk', note: 'Player left before paying.', lines: s8Lines, placedAt: ago(140), cancelledAt: ago(138) },
  ];
}

// ---------------------------------------------------------------------------------------------
// The kitchen display (spec/13 §6.2): five live tickets across New, Preparing and Ready, two to recall
// ---------------------------------------------------------------------------------------------

interface ItemSpec {
  readonly tail: string;
  readonly name: string;
  readonly quantity: number;
  readonly modifiers?: readonly string[];
  readonly note?: string;
}

function ticket(
  tail: string,
  orderTail: string,
  area: KitchenTicket['area'],
  state: KitchenTicketState,
  stationId: string | null,
  times: { readonly placed: number; readonly accepted?: number; readonly preparing?: number; readonly ready?: number; readonly delivered?: number },
  items: readonly ItemSpec[],
  note: string | null = null,
  sale?: { readonly tail: string; readonly orderNumber: number; readonly paid: boolean },
): KitchenTicket {
  const latest = Math.min(times.placed, times.accepted ?? Infinity, times.ready ?? Infinity, times.delivered ?? Infinity);
  return {
    id: id('ktk', tail),
    orderId: sale === undefined ? id('fod', orderTail) : null,
    saleId: sale === undefined ? null : id('sal', sale.tail),
    orderNumber: sale?.orderNumber ?? null,
    paid: sale?.paid ?? false,
    venueId: VENUE_ID,
    area,
    state,
    stationId,
    note,
    items: items.map((item, index) => ({ id: id('kti', item.tail), name: item.name, quantity: item.quantity, modifiers: [...(item.modifiers ?? [])], note: item.note ?? null, sortOrder: index })),
    placedAt: ago(times.placed),
    acceptedAt: times.accepted === undefined ? null : ago(times.accepted),
    preparingAt: times.preparing === undefined ? null : ago(times.preparing),
    readyAt: times.ready === undefined ? null : ago(times.ready),
    deliveredAt: times.delivered === undefined ? null : ago(times.delivered),
    updatedAt: ago(latest),
  };
}

function seedTickets(): KitchenTicket[] {
  return [
    ticket('k01', '6a1', 'kitchen', 'new', STATION_PC04, { placed: 2 }, [{ tail: 'e11', name: 'Chicken biryani', quantity: 1, modifiers: ['Raita'] }]),
    ticket('k02', '6a1', 'bar', 'new', STATION_PC04, { placed: 2 }, [{ tail: 'e21', name: 'Masala chai', quantity: 1, note: 'Less sugar' }]),
    ticket('k03', '6a3', 'kitchen', 'preparing', null, { placed: 7, accepted: 6 }, [
      { tail: 'e31', name: 'Masala fries', quantity: 2 },
      { tail: 'e32', name: 'Paneer tikka sandwich', quantity: 1, note: 'No onion' },
    ]),
    ticket(
      'k04',
      '6a4',
      'kitchen',
      'preparing',
      STATION_PC07,
      { placed: 12, accepted: 10 },
      [
        { tail: 'e41', name: 'Veg hakka noodles', quantity: 1 },
        { tail: 'e42', name: 'Egg fried rice', quantity: 1 },
      ],
      'Serve both together.',
    ),
    ticket('k05', '6a5', 'bar', 'ready', COMMERCE_STATION_PC01, { placed: 9, accepted: 8, ready: 1 }, [
      { tail: 'e51', name: 'Cold coffee', quantity: 2, modifiers: ['Large'] },
      { tail: 'e52', name: 'Fresh lime soda', quantity: 1, modifiers: ['Sweet'] },
    ]),
    ticket('k06', '6a6', 'kitchen', 'delivered', COMMERCE_STATION_PC01, { placed: 40, accepted: 39, ready: 30, delivered: 28 }, [{ tail: 'e61', name: 'Chicken nuggets (6 pc)', quantity: 2 }]),
    ticket('k07', '6a7', 'bar', 'delivered', null, { placed: 50, accepted: 49, ready: 45, delivered: 44 }, [{ tail: 'e71', name: 'Masala chai', quantity: 3 }]),
    ticket('k08', '5a1', 'bar', 'accepted', COMMERCE_STATION_PC01, { placed: 4, accepted: 3 }, [{ tail: 'e81', name: 'Cold coffee', quantity: 1 }], null, {
      tail: '5a1',
      orderNumber: 421,
      paid: false,
    }),
  ];
}

/** The states the board shows with no filter (spec/13 §6.2). */
const ACTIVE_STATES: ReadonlySet<KitchenTicketState> = new Set(['new', 'accepted', 'preparing', 'ready']);
/** A ticket moves one step on, or is recalled one step back (spec/13 §6.2, F-FNB-04); any open one may be cancelled. */
const FORWARD: Partial<Record<KitchenTicketState, KitchenTicketState>> = { new: 'accepted', accepted: 'preparing', preparing: 'ready', ready: 'delivered' };
const BACKWARD: Partial<Record<KitchenTicketState, KitchenTicketState>> = { accepted: 'new', preparing: 'accepted', ready: 'preparing', delivered: 'ready' };

// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------

const ORGANIZATION_PATH = `/v1/organizations/${ORGANIZATION_ID}`;
const VENUE_PATH = `${ORGANIZATION_PATH}/venues/${VENUE_ID}`;

/** The paths this file answers, for a caller that routes by pattern. */
export const COMMERCE_PATHS: readonly RegExp[] = [
  new RegExp(`^${ORGANIZATION_PATH}/product-categories$`),
  new RegExp(`^${ORGANIZATION_PATH}/product-categories/[^/]+$`),
  new RegExp(`^${ORGANIZATION_PATH}/products$`),
  new RegExp(`^${ORGANIZATION_PATH}/products/[^/]+/availability$`),
  new RegExp(`^${ORGANIZATION_PATH}/products/[^/]+$`),
  new RegExp(`^${VENUE_PATH}/menu$`),
  new RegExp(`^${VENUE_PATH}/menu/products/[^/]+/availability$`),
  new RegExp(`^${VENUE_PATH}/sales$`),
  new RegExp(`^${VENUE_PATH}/sales/[^/]+$`),
  new RegExp(`^${VENUE_PATH}/sales/[^/]+/(payments|on-account|cancel|refunds|document|document/send|document/print|invoice/gstin|discount-code)$`),
  new RegExp(`^${VENUE_PATH}/counter-upi$`),
  new RegExp(`^${VENUE_PATH}/kitchen/tickets$`),
  new RegExp(`^${VENUE_PATH}/kitchen/tickets/[^/]+/state$`),
  new RegExp(`^${VENUE_PATH}/food-orders/[^/]+(/cancel)?$`),
];

export function createFakeCommerce(helpers: CommerceHelpers) {
  const { json, problem, gate } = helpers;
  const can = helpers.can ?? (() => true);

  const state = {
    products: [...COMMERCE_PRODUCTS] as MenuProduct[],
    categories: [...COMMERCE_CATEGORIES] as MenuCategory[],
    sales: seedSales(),
    tickets: seedTickets(),
    /** Food orders already paid (cannot be cancelled), and those cancelled here (F-POS-15). */
    settledOrders: new Set<string>([id('fod', '6a5')]),
    cancelledOrders: new Set<string>(),
    /** Sale id → its document, issued when a payment covers the total (spec/06 §10.2). */
    documents: new Map<string, SaleDocument>(),
    nextDocument: 41,
    /**
     * Whether a sale placed here accepts a cash overpayment's change into its player's wallet (F-POS-12, ADR-0036):
     * the server's `changeToWalletAvailable` on the sale read. Off by default, as a venue whose wallet is off.
     */
    changeToWalletAvailable: false,
    /** The venue's own UPI ID for the Sell QR (`payments.counterUpiVpa`, F-PAY-02). */
    counterUpiVpa: 'arenaindiranagar@okhdfcbank' as string | null,
    /** The last number handed to a row a mutation created. */
    serial: 0,
    /**
     * Of each new document's GST, how much was paid at top-up (a café that moved its balances in as GST already
     * paid): the server's `totals.taxPaidAtTopUp`. Zero, and absent from the document, by default.
     */
    documentTaxPaidAtTopUp: 0,
  };

  function fresh(prefix: string): string {
    state.serial += 1;
    return numberedId(prefix, state.serial);
  }

  function issueDocument(sale: Sale): void {
    if (sale.paidAt === null || state.documents.has(sale.id)) return;
    const number = state.nextDocument;
    state.nextDocument += 1;
    state.documents.set(sale.id, {
      id: id('sdc', `d${String(number).padStart(2, '0')}`),
      saleId: sale.id,
      kind: 'tax_invoice',
      title: 'Tax invoice',
      taxMode: 'regular',
      series: `tax_invoice:${GSTIN}:${FINANCIAL_YEAR}`,
      number,
      formattedNumber: `INV/${FINANCIAL_YEAR}/${String(number).padStart(6, '0')}`,
      gstin: GSTIN,
      financialYear: FINANCIAL_YEAR,
      issuedAt: sale.paidAt,
      lastRequestedChannel: null,
      lastRequestedAt: null,
      delivery: null,
      ...documentContentOf(sale),
    });
  }

  /** What the document shows (F-CMP-07), from the sale as the server's `documentContent` builds it. */
  function documentContentOf(sale: Sale): Pick<SaleDocument, 'venueName' | 'buyer' | 'lines' | 'totals' | 'payments' | 'creditNotes'> {
    const summary = new Map<string, { component: 'CGST' | 'SGST' | 'IGST'; rateBps: number; taxable: number; amount: number }>();
    for (const line of sale.lines) {
      for (const component of line.taxComponents) {
        const key = `${component.component}:${component.rateBps}`;
        const row = summary.get(key) ?? { component: component.component, rateBps: component.rateBps, taxable: 0, amount: 0 };
        row.taxable += line.taxable.amount;
        row.amount += component.amount.amount;
        summary.set(key, row);
      }
    }
    return {
      venueName: 'Arena Indiranagar',
      buyer: sale.buyer,
      lines: sale.lines.map((line) => ({
        name: line.name,
        hsnSac: line.hsnSac,
        quantity: line.quantity,
        unitPrice: line.unitPrice,
        rateBps: line.taxRateBps,
        taxable: line.taxable,
        tax: line.tax,
        taxComponents: line.taxComponents,
        total: line.total,
      })),
      totals: {
        taxable: sale.subtotal,
        tax: sale.tax,
        gross: inr(sale.subtotal.amount + sale.tax.amount),
        discount: sale.discount,
        total: sale.total,
        taxSummary: [...summary.values()].map((row) => ({ component: row.component, rateBps: row.rateBps, taxable: inr(row.taxable), amount: inr(row.amount) })),
        ...(state.documentTaxPaidAtTopUp > 0 ? { taxPaidAtTopUp: inr(state.documentTaxPaidAtTopUp) } : {}),
      },
      payments: sale.payments.map((payment) => ({ tender: payment.tender, amount: payment.amount })),
      creditNotes: [],
    };
  }
  // Documents are numbered in the order the sales were paid, oldest first.
  for (const sale of state.sales.map(toSale).filter((one) => one.paidAt !== null).sort((a, b) => (a.paidAt ?? '').localeCompare(b.paidAt ?? ''))) {
    issueDocument(sale);
  }

  const invalid = (error: z.ZodError) =>
    problem(400, 'validation_failed', 'Validation failed', {
      detail: 'The request could not be understood as written.',
      errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
    });
  const forbidden = () => problem(403, 'forbidden', 'Not allowed');
  const methodNotAllowed = () => problem(405, 'method_not_allowed', 'Method not allowed');
  const conflict = (detail: string) => problem(409, 'conflict', 'Conflict', { detail });

  function venueMenu() {
    return { categories: state.categories, products: state.products };
  }

  function replaceSale(next: SaleRecord): Sale {
    state.sales = state.sales.map((one) => (one.id === next.id ? next : one));
    const sale = toSale(next);
    issueDocument(sale);
    return sale;
  }

  /** A cart's lines priced as the server prices them (F-POS-01), or the refusal — shared by a sale and its quote. */
  function priceSaleLines(data: z.infer<typeof CreateSaleSchema>): SaleLine[] | FakeResponse {
    const lines: SaleLine[] = [];
    for (const line of data.lines) {
      const lineId = fresh('sln');
      if (line.passProductId !== undefined) {
        // A pass line (F-POS-01): priced from the pass product, never the client; the sale's player owns it — or, a guest
        // pass line (F-SES-07), a walk-in with no account, on a sale that names nobody.
        if (line.guest === true) {
          if (helpers.guests?.enabled() !== true) return problem(409, 'guests_off', 'This venue does not serve guests.');
          if (data.playerId !== undefined) return problem(400, 'validation_failed', 'A guest pass is sold on a sale with no player.');
        } else if (data.playerId === undefined) return problem(400, 'validation_failed', 'A pass line needs a player on the sale.');
        if (line.kind !== 'time' || line.quantity !== 1) return problem(400, 'validation_failed', 'A pass line is a time line of quantity 1.');
        const pass = helpers.passes?.priceForCounter(line.passProductId);
        if (pass === undefined) return problem(404, 'not_found', 'No such pass here.');
        if ('refused' in pass) return conflict(pass.refused);
        const priced = priceLine({ tail: '000', kind: 'time', name: pass.name, unitPrice: pass.price.amount, taxRateBps: 0, quantity: 1 });
        lines.push({ ...priced, id: lineId, passProductId: line.passProductId });
        continue;
      }
      if (line.kind === 'food' || line.kind === 'retail') {
        const found = line.productId === undefined ? undefined : state.products.find((candidate) => candidate.id === line.productId);
        if (found === undefined) return problem(404, 'not_found', 'Not on this venue’s menu');
        if (!found.available) return conflict(`${found.name} is sold out.`);
        const modifiers = found.modifierGroups.flatMap((group) => group.modifiers).filter((modifier) => (line.modifierIds ?? []).some((chosen) => chosen === modifier.id));
        const variant = found.variants.find((candidate) => candidate.id === line.variantId);
        const priced = priceLine({
          tail: '000',
          product: variant === undefined ? found : { ...found, price: variant.price },
          kind: line.kind,
          quantity: line.quantity,
          modifiers: modifiers.map((modifier) => ({ name: modifier.name, price: modifier.price.amount })),
        });
        lines.push({ ...priced, id: lineId, variantId: variant?.id ?? null });
      } else {
        if (line.name === undefined || line.unitPrice === undefined) return conflict('A time or other line needs its name and unit price.');
        if (line.unitPrice.currency !== CURRENCY) return conflict('The sale is in INR.');
        const taxRateBps = line.taxRateBps ?? (line.kind === 'time' ? TIME_RATE_BPS : 0);
        const priced = priceLine({ tail: '000', kind: line.kind, name: line.name, unitPrice: line.unitPrice.amount, taxRateBps, quantity: line.quantity });
        lines.push({ ...priced, id: lineId });
      }
    }
    return lines;
  }

  function createSale(input: Record<string, unknown>): FakeResponse {
    const parsed = CreateSaleSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const lines = priceSaleLines(parsed.data);
    if (!Array.isArray(lines)) return lines;
    const record: SaleRecord = {
      id: fresh('sal'),
      channel: parsed.data.channel ?? 'counter',
      playerId: parsed.data.playerId ?? null,
      stationId: parsed.data.stationId ?? null,
      note: parsed.data.note ?? null,
      lines,
      payments: [],
      discounts: [],
      refunds: [],
      placedAt: NOW,
      cancelledAt: null,
      // Every sale read the checkout uses says whether its change may go to a wallet, and why not (ADR-0049).
      changeToWalletAvailable: state.changeToWalletAvailable,
    };
    state.sales.unshift(record);
    // Guest pass lines (F-SES-07): a guest and its ticket code for each, minted with the sale as the server does.
    parsed.data.lines.forEach((line, index) => {
      const priced = lines[index];
      if (line.guest !== true || line.passProductId === undefined || priced === undefined) return;
      helpers.guests?.mint(record.id, { name: priced.name, price: priced.total, seconds: 3600, productId: line.passProductId });
    });
    return json(201, toSale(record));
  }

  /** A cart priced with its automatic promotions and any code, before it is placed (F-POS-03): nothing is written. */
  function quoteSale(input: Record<string, unknown>): FakeResponse {
    const parsed = QuoteSaleSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const { discountCode, ...cart } = parsed.data;
    const lines = priceSaleLines(cart);
    if (!Array.isArray(lines)) return lines;
    const gross = sum(lines.map((line) => line.total));
    const promoted = helpers.promotions?.forCart({
      lines: lines.map((line) => ({ kind: line.kind, productId: line.productId, gross: line.total.amount })),
      channel: cart.channel ?? 'counter',
      venueId: VENUE_ID,
      at: NOW,
    }) ?? { promotions: [], lineDiscounts: [] };
    const promotionDiscount = sum(promoted.promotions.map((one) => one.amount));
    let code: SaleQuote['discountCode'] = null;
    if (discountCode !== undefined) {
      const priced = helpers.codes === undefined ? 'unknown' : helpers.codes.priceCode(discountCode, gross.amount - promotionDiscount.amount);
      if (priced === 'unknown') return problem(404, 'not_found', 'No such code.');
      if (priced === 'refused') return conflict('The code is not valid here.');
      code = { code: priced.code, amount: priced.amount };
    }
    const discount = inr(promotionDiscount.amount + (code?.amount.amount ?? 0));
    const quote: SaleQuote = {
      lines: lines.map((line, index) => ({ name: line.name, quantity: line.quantity, gross: line.total, promotionDiscount: inr(promoted.lineDiscounts[index] ?? 0) })),
      gross,
      tax: sum(lines.map((line) => line.tax)),
      promotions: [...promoted.promotions],
      discountCode: code,
      discount,
      total: inr(Math.max(0, gross.amount - discount.amount)),
    };
    return json(200, quote);
  }

  function takePayment(record: SaleRecord, input: Record<string, unknown>): FakeResponse {
    const parsed = TakeSalePaymentSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const sale = toSale(record);
    if (sale.status !== 'placed' && sale.status !== 'partially_paid') return conflict('The sale is cancelled or already paid.');
    const { tender, amount, tendered, reference, changeToWallet, changeToWalletPlayerId } = parsed.data;
    if (amount.currency !== CURRENCY || amount.amount <= 0 || amount.amount > sale.outstanding.amount) return conflict('A tender cannot exceed the outstanding amount.');
    if (tendered !== undefined && tendered.amount < amount.amount) return conflict('Less cash was tendered than the amount taken.');
    if (tender === 'wallet') {
      if (helpers.wallet === undefined) return problem(409, 'wallet_disabled', 'The wallet is not enabled here');
      if (record.playerId == null) return problem(400, 'validation_failed', 'A wallet tender needs a sale with a player.');
      if (helpers.wallet.spend(record.playerId, amount.amount, `Sale ${record.id}`) === 'insufficient') {
        return problem(409, 'wallet_insufficient_funds', 'The wallet does not hold enough');
      }
    }
    // A cash overpayment's change into the player's wallet (F-POS-12, ADR-0036), refused as the server refuses it: not
    // cash, no overpayment, no player, or a venue that does not accept it; over the wallet's limit is `wallet_limit`.
    const overpaid = tender === 'cash' && tendered !== undefined ? tendered.amount - amount.amount : 0;
    if (changeToWallet === true) {
      // The sale's player, or on an anonymous sale the one picked at checkout (ADR-0049); a different one is refused.
      const changePlayer = record.playerId ?? changeToWalletPlayerId ?? null;
      const mismatch = record.playerId != null && changeToWalletPlayerId !== undefined && changeToWalletPlayerId !== record.playerId;
      if (tender !== 'cash' || overpaid <= 0 || changePlayer === null || mismatch || record.changeToWalletAvailable !== true || helpers.wallet?.creditRefund === undefined) {
        return problem(409, 'change_to_wallet_unavailable', 'The change cannot go to the wallet here.');
      }
      if (helpers.wallet.creditRefund(changePlayer, overpaid, overpaid, `Change from sale ${record.id}`) === 'over-balance') {
        return problem(409, 'wallet_limit', 'That would take the wallet over its limit.');
      }
    }
    const payment: SalePayment = {
      id: fresh('spy'),
      tender,
      amount,
      // The change went to the wallet: as the server, the payment records no cash hand-over (spec/06 §9) but says
      // where the change went, so the receipt reads "Cash received" and "Change added to <player>'s wallet" (ADR-0049).
      tendered: tender === 'cash' && changeToWallet !== true ? (tendered ?? amount) : null,
      change: tender === 'cash' && changeToWallet !== true ? inr((tendered ?? amount).amount - amount.amount) : null,
      ...(changeToWallet === true ? { changeToWallet: { amount: inr(overpaid), playerId: (record.playerId ?? changeToWalletPlayerId) as NonNullable<SalePayment['changeToWallet']>['playerId'] } } : {}),
      reference: tender === 'counter_upi' || tender === 'counter_card' ? (reference ?? null) : null,
      status: 'succeeded',
      createdAt: NOW,
    };
    const paid = replaceSale({ ...record, payments: [...record.payments, payment] });
    // The sale paid: its guests' codes work from now (F-SES-07), as the server's payment handler makes them.
    if (paid.status === 'paid') helpers.guests?.activate(record.id);
    return json(200, paid);
  }

  /**
   * Refund a paid sale (F-POS-08): to a counter tender, or to the player's wallet as cash (2026-10-02). The part of a
   * wallet refund that is not wallet money going back is held to the wallet's maximum balance, as the server does.
   */
  function refundSale(record: SaleRecord, input: Record<string, unknown>): FakeResponse {
    const parsed = RefundSaleSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const sale = toSale(record);
    if (sale.status !== 'paid' && sale.status !== 'partially_refunded') return conflict('This sale is not paid; there is nothing to refund.');
    const { tender, reason, playerId } = parsed.data;
    const amount = parsed.data.amount;
    if (amount === undefined) return problem(400, 'validation_failed', 'The fake prices a refund by amount only.');
    if (amount.amount <= 0 || amount.amount > sale.paid.amount - sale.refunded.amount) return conflict('The refund is more than what remains refundable.');
    if (tender === 'online') {
      // Back to the player's online payment (Cashfree): at most what was paid online and not yet refunded online.
      const onlinePaid = record.payments.filter((payment) => payment.tender === 'online' && payment.status === 'succeeded').reduce((sum, payment) => sum + payment.amount.amount, 0);
      const onlineBack = record.refunds.filter((one) => one.tender === 'online' && one.status !== 'declined').reduce((sum, one) => sum + one.amount.amount, 0);
      if (amount.amount > onlinePaid - onlineBack) return conflict('The refund is more than was paid online and not yet refunded online.');
    }
    if (playerId !== undefined && tender !== 'wallet') return problem(400, 'validation_failed', 'A player is named only for a refund to the wallet.');
    let next: SaleRecord = record;
    if (tender === 'wallet') {
      if (helpers.wallet?.creditRefund === undefined) return problem(409, 'wallet_disabled', 'The wallet is not enabled here');
      if (record.playerId != null && playerId !== undefined && playerId !== record.playerId) {
        return problem(400, 'validation_failed', 'This sale already names its player.');
      }
      const player = record.playerId ?? playerId;
      if (player === undefined) return problem(400, 'validation_failed', 'A refund to the wallet needs a sale with a player.');
      const walletPaid = record.payments.filter((payment) => payment.tender === 'wallet').reduce((sum, payment) => sum + payment.amount.amount, 0);
      const walletReturned = record.refunds.filter((one) => one.tender === 'wallet' && one.status === 'succeeded').reduce((sum, one) => sum + one.amount.amount, 0);
      const newMoney = amount.amount - Math.min(amount.amount, Math.max(0, walletPaid - walletReturned));
      if (helpers.wallet.creditRefund(player, amount.amount, newMoney, `Refund of sale ${record.id}`) === 'over-balance') {
        return problem(409, 'wallet_limit', 'Top-up outside the wallet’s limits', { detail: 'Refunding this to the wallet would take it over its maximum balance.' });
      }
      next = { ...record, playerId: player };
    }
    const refund: SaleRefund = { id: fresh('srf'), status: 'succeeded', tender, amount, reason, lines: [], requestedAt: NOW, settledAt: NOW };
    return json(201, replaceSale({ ...next, refunds: [...next.refunds, refund] }));
  }

  /** The on-account tender (F-POS-07): the outstanding, or a part, charged to the player's account. */
  function takeOnAccount(record: SaleRecord, input: Record<string, unknown>): FakeResponse {
    const parsed = TakeSaleOnAccountSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const sale = toSale(record);
    if (sale.status !== 'placed' && sale.status !== 'partially_paid') return conflict('The sale is cancelled or already paid.');
    if (record.playerId == null) return problem(400, 'validation_failed', 'An on-account sale must name a registered player.');
    const amount = parsed.data.amount ?? sale.outstanding;
    if (amount.currency !== CURRENCY || amount.amount <= 0 || amount.amount > sale.outstanding.amount) return conflict('A charge cannot exceed the outstanding amount.');
    const charged = helpers.credit === undefined ? 'disabled' : helpers.credit.charge(record.playerId, amount.amount);
    if (charged === 'disabled') return problem(409, 'credit_disabled', 'On account is off here');
    if (charged === 'over-limit') return problem(409, 'credit_limit_exceeded', 'Over the player’s credit limit');
    const payment: SalePayment = { id: fresh('spy'), tender: 'on_account', amount, tendered: null, change: null, reference: null, status: 'succeeded', createdAt: NOW };
    return json(200, replaceSale({ ...record, payments: [...record.payments, payment] }));
  }

  /** A discount code on a placed, unpaid sale (F-POS-02): one per sale, before any tender, priced by the codes fake. */
  function applyCode(record: SaleRecord, input: Record<string, unknown>): FakeResponse {
    const parsed = ApplyDiscountCodeSchema.safeParse(input);
    if (!parsed.success) return invalid(parsed.error);
    const sale = toSale(record);
    if (sale.status !== 'placed' || record.discounts.some((one) => one.kind === 'code')) return conflict('The sale is paid or cancelled, or a code is already applied.');
    const priced = helpers.codes === undefined ? 'unknown' : helpers.codes.priceCode(parsed.data.code, sale.total.amount);
    if (priced === 'unknown') return problem(404, 'not_found', 'No such code.');
    if (priced === 'refused') return conflict('The code is not valid here.');
    const discount: SaleDiscount = { kind: 'code', code: priced.code, type: priced.type, reason: null, amount: priced.amount };
    return json(200, replaceSale({ ...record, discounts: [...record.discounts, discount] }));
  }

  /** `undefined` for a path that is not venue commerce's. */
  function handle(method: string, pathname: string, query: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    if (!COMMERCE_PATHS.some((pattern) => pattern.test(pathname))) return undefined;
    const refused = gate();
    if (refused !== undefined) return refused;

    // -- the menu, organisation-scoped (spec/13 §6.1) ---------------------------------------
    if (pathname === `${ORGANIZATION_PATH}/product-categories`) {
      if (method !== 'GET') return undefined; // createMenuCategory is not served.
      if (!can('products:read')) return forbidden();
      return json(200, { categories: state.categories });
    }
    // Edit a category (F-FNB-02): only the fields sent change, merged onto the category for the next read.
    const categoryItem = new RegExp(`^${ORGANIZATION_PATH}/product-categories/([^/]+)$`).exec(pathname);
    if (categoryItem !== null) {
      if (method !== 'PATCH') return undefined;
      if (!can('products:manage')) return forbidden();
      const found = state.categories.find((candidate) => candidate.id === categoryItem[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such category in this organisation.');
      const saved: MenuCategory = { ...found, ...(input as Partial<MenuCategory>) };
      state.categories = state.categories.map((candidate) => (candidate.id === saved.id ? saved : candidate));
      return json(200, saved);
    }
    if (pathname === `${ORGANIZATION_PATH}/products`) {
      if (method !== 'GET') return undefined; // createMenuProduct is not served.
      if (!can('products:read')) return forbidden();
      return json(200, { products: state.products });
    }
    const availability = new RegExp(`^${ORGANIZATION_PATH}/products/([^/]+)/availability$`).exec(pathname);
    if (availability !== null) {
      if (method !== 'PATCH') return methodNotAllowed();
      if (!can('products:availability')) return forbidden();
      const parsed = SetProductAvailabilitySchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const found = state.products.find((candidate) => candidate.id === availability[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such product in this organisation.');
      const saved: MenuProduct = { ...found, available: parsed.data.available };
      state.products = state.products.map((candidate) => (candidate.id === saved.id ? saved : candidate));
      return json(200, saved);
    }
    // Edit a product (F-FNB-01): only the fields sent change, merged onto the product for the next read.
    const productItem = new RegExp(`^${ORGANIZATION_PATH}/products/([^/]+)$`).exec(pathname);
    if (productItem !== null) {
      if (method !== 'PATCH') return undefined; // getMenuProduct is not served.
      if (!can('products:manage')) return forbidden();
      const found = state.products.find((candidate) => candidate.id === productItem[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such product in this organisation.');
      // A picture is attached by file id (F-FNB-01) and read back as its URLs, as the server does.
      const { imageFileId, ...rest } = input as Partial<MenuProduct> & { imageFileId?: string | null };
      const saved: MenuProduct = {
        ...found,
        ...rest,
        ...(imageFileId === undefined ? {} : { image: imageFileId === null ? null : { fileId: imageFileId, url: `https://cdn.test/${imageFileId}.webp`, variants: [] } }),
      };
      state.products = state.products.map((candidate) => (candidate.id === saved.id ? saved : candidate));
      return json(200, saved);
    }
    if (pathname === `${VENUE_PATH}/menu`) {
      if (method !== 'GET') return undefined; // putVenueMenuOverrides is not served.
      if (!can('products:read')) return forbidden();
      return json(200, venueMenu());
    }
    // The venue's own sold-out switch (F-FNB-03): this venue's availability, never the product's.
    const venueAvailability = new RegExp(`^${VENUE_PATH}/menu/products/([^/]+)/availability$`).exec(pathname);
    if (venueAvailability !== null) {
      if (method !== 'PATCH') return methodNotAllowed();
      if (!can('products:availability')) return forbidden();
      const parsed = SetProductAvailabilitySchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const found = state.products.find((candidate) => candidate.id === venueAvailability[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such product in this organisation.');
      const saved: MenuProduct = { ...found, available: parsed.data.available };
      state.products = state.products.map((candidate) => (candidate.id === saved.id ? saved : candidate));
      return json(200, saved);
    }

    // -- sales (spec/06 §8) -----------------------------------------------------------------
    if (pathname === `${VENUE_PATH}/sales`) {
      if (method === 'GET') {
        if (!can('orders:read')) return forbidden();
        // `awaiting=payment` (F-POS-10): the pending kiosk/app orders — placed, nothing paid — oldest first.
        if (query.get('awaiting') === 'payment') {
          const pending = state.sales
            .map(toSale)
            .filter(
              (sale) =>
                ((sale.channel === 'kiosk' || sale.channel === 'app') && sale.status === 'placed' && sale.paid.amount === 0) ||
                // The one order a console session placed when it ended (ADR-0034), until it is settled.
                (sale.origin === 'console_session' && (sale.status === 'placed' || sale.status === 'partially_paid')),
            )
            .sort((a, b) => a.placedAt.localeCompare(b.placedAt));
          return json(200, { sales: pending });
        }
        return json(200, { sales: state.sales.map(toSale) });
      }
      if (method !== 'POST') return methodNotAllowed();
      if (!can('orders:create')) return forbidden();
      return createSale(input);
    }
    if (pathname === `${VENUE_PATH}/sales/quote`) {
      if (method !== 'POST') return methodNotAllowed();
      if (!can('orders:create')) return forbidden();
      return quoteSale(input);
    }
    // The venue's own UPI QR (F-PAY-02): the fixtures' venue has set its UPI ID.
    if (pathname === `${VENUE_PATH}/counter-upi`) {
      if (method !== 'GET') return methodNotAllowed();
      if (!can('orders:read')) return forbidden();
      return json(200, { vpa: state.counterUpiVpa, payeeName: 'Arena Indiranagar' });
    }
    const saleMatch = new RegExp(`^${VENUE_PATH}/sales/([^/]+)(?:/(payments|on-account|cancel|refunds|document|document/send|document/print|invoice/gstin|discount-code))?$`).exec(pathname);
    if (saleMatch !== null) {
      const [, saleId, action] = saleMatch;
      const record = state.sales.find((one) => one.id === saleId);
      if (action === undefined) {
        if (method !== 'GET') return methodNotAllowed();
        if (!can('orders:read')) return forbidden();
        return record === undefined ? problem(404, 'not_found', 'No such sale at this venue.') : json(200, toSale(record));
      }
      if (action === 'document') {
        if (method !== 'GET') return methodNotAllowed();
        if (!can('orders:read')) return forbidden();
        const document = record === undefined ? undefined : state.documents.get(record.id);
        return document === undefined ? problem(404, 'not_found', 'No such sale, or it has no document yet (it is not paid).') : json(200, document);
      }
      if (action === 'document/print') {
        // The server renders the page (F-CMP-07); the fake answers a small one with the document's number.
        if (method !== 'GET') return methodNotAllowed();
        if (!can('orders:read')) return forbidden();
        const document = record === undefined ? undefined : state.documents.get(record.id);
        if (document === undefined) return problem(404, 'not_found', 'No such sale, or it has no document yet (it is not paid).');
        const layout = query.get('layout') ?? 'receipt';
        return json(200, `<!doctype html><title>${document.title}</title><body class="layout-${layout}">${document.formattedNumber}</body>`);
      }
      if (action === 'invoice/gstin') {
        // A B2B buyer before the first payment (F-CMP-07); another state than Karnataka's (29) is IGST.
        if (method !== 'PUT') return methodNotAllowed();
        if (!can('orders:create')) return forbidden();
        if (record === undefined) return problem(404, 'not_found', 'No such sale at this venue.');
        const body = (input ?? {}) as { gstin?: string; name?: string };
        if (typeof body.gstin !== 'string' || !/^[0-9]{2}[A-Z0-9]{13}$/.test(body.gstin) || typeof body.name !== 'string' || body.name.trim() === '') {
          return problem(400, 'validation_failed', 'Validation failed');
        }
        if (toSale(record).status !== 'placed') return conflict('The buyer is set before the first payment.');
        const inter = !body.gstin.startsWith('29');
        const lines = record.lines.map((line) =>
          inter && line.tax.amount > 0 ? { ...line, taxComponents: [{ component: 'IGST' as const, rateBps: line.taxRateBps, amount: line.tax }] } : line,
        );
        return json(200, replaceSale({ ...record, lines, buyer: { name: body.name.trim(), gstin: body.gstin, placeOfSupply: inter ? 'inter_state' : 'intra_state' } }));
      }
      if (method !== 'POST') return methodNotAllowed();
      if (action === 'document/send') {
        // The server queues the email and the provider answers after the commit (F-POS-13); the fake
        // answers at once, as the provider would: sent to the typed address, or refused with no address.
        if (!can('orders:read')) return forbidden();
        const document = record === undefined ? undefined : state.documents.get(record.id);
        if (record === undefined || document === undefined) return problem(404, 'not_found', 'No such sale, or it has no document yet.');
        const body = (input ?? {}) as { channel?: string; to?: string };
        if (body.channel !== 'email') return conflict('Receipts cannot be sent by whatsapp yet. Send it by email.');
        const to = body.to?.trim();
        if (to === undefined || to === '') return conflict('There is no email address to send the receipt to. Enter one.');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) return problem(400, 'validation_failed', 'Enter an email address to send the receipt to.');
        const masked = `${to.slice(0, 1)}•••@${to.slice(to.lastIndexOf('@') + 1)}`;
        const sent = {
          ...document,
          lastRequestedChannel: 'email',
          lastRequestedAt: NOW,
          delivery: { status: 'sent' as const, channel: 'email' as const, to: masked, auto: false, requestedAt: NOW, completedAt: NOW, failure: null },
        };
        state.documents.set(record.id, sent);
        return json(200, sent);
      }
      if (action === 'refunds') {
        if (method !== 'POST') return methodNotAllowed();
        if (!can('orders:refund')) return forbidden();
        return record === undefined ? problem(404, 'not_found', 'No such sale at this venue.') : refundSale(record, input);
      }
      if (action === 'payments') {
        if (!can('orders:create')) return forbidden();
        return record === undefined ? problem(404, 'not_found', 'No such sale at this venue.') : takePayment(record, input);
      }
      if (action === 'discount-code') {
        if (!can('orders:create')) return forbidden();
        return record === undefined ? problem(404, 'not_found', 'No such sale at this venue.') : applyCode(record, input);
      }
      if (action === 'on-account') {
        if (!can('orders:on_account')) return forbidden();
        return record === undefined ? problem(404, 'not_found', 'No such sale at this venue.') : takeOnAccount(record, input);
      }
      // cancel
      if (!can('orders:void')) return forbidden();
      if (record === undefined) return problem(404, 'not_found', 'No such sale at this venue.');
      if (toSale(record).status !== 'placed') return conflict('The sale has taken a payment, or is already cancelled.');
      return json(200, replaceSale({ ...record, cancelledAt: NOW }));
    }

    // -- the kitchen display (spec/13 §6.2) -------------------------------------------------
    if (pathname === `${VENUE_PATH}/kitchen/tickets`) {
      if (method !== 'GET') return methodNotAllowed();
      if (!can('kitchen:read')) return forbidden();
      const wantedState = query.get('state');
      const wantedArea = query.get('area');
      const stateFilter = wantedState === null ? undefined : KitchenTicketStateSchema.safeParse(wantedState);
      const areaFilter = wantedArea === null ? undefined : FoodPreparationAreaSchema.safeParse(wantedArea);
      if (stateFilter?.success === false) return invalid(stateFilter.error);
      if (areaFilter?.success === false) return invalid(areaFilter.error);
      const tickets = state.tickets
        .filter((one) => (stateFilter === undefined ? ACTIVE_STATES.has(one.state) : one.state === stateFilter.data))
        .filter((one) => areaFilter === undefined || one.area === areaFilter.data)
        .sort((a, b) => a.placedAt.localeCompare(b.placedAt));
      return json(200, { tickets });
    }
    // -- a food order, read back from its tickets (F-POS-15) ---------------------------------
    const foodOrder = new RegExp(`^${VENUE_PATH}/food-orders/([^/]+)(/cancel)?$`).exec(pathname);
    if (foodOrder !== null) {
      const orderId = foodOrder[1] ?? '';
      const order = foodOrderOf(orderId);
      if (foodOrder[2] === undefined) {
        if (method !== 'GET') return methodNotAllowed();
        if (!can('orders:read')) return forbidden();
        return order === undefined ? problem(404, 'not_found', 'No such order at this venue.') : json(200, order);
      }
      if (method !== 'POST') return methodNotAllowed();
      if (!can('orders:void')) return forbidden();
      if (order === undefined) return problem(404, 'not_found', 'No such order at this venue.');
      if (order.status === 'cancelled' || order.settlementStatus === 'settled') return conflict('The order is already settled or already cancelled.');
      state.cancelledOrders.add(orderId);
      state.tickets = state.tickets.map((one) => (one.orderId === orderId && one.state !== 'delivered' ? { ...one, state: 'cancelled', updatedAt: NOW } : one));
      return json(200, foodOrderOf(orderId));
    }

    const advance = new RegExp(`^${VENUE_PATH}/kitchen/tickets/([^/]+)/state$`).exec(pathname);
    if (advance !== null) {
      if (method !== 'POST') return methodNotAllowed();
      if (!can('kitchen:update')) return forbidden();
      const parsed = AdvanceKitchenTicketSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const found = state.tickets.find((one) => one.id === advance[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such kitchen ticket at this venue.');
      const to = parsed.data.state;
      const legal = FORWARD[found.state] === to || BACKWARD[found.state] === to || (to === 'cancelled' && found.state !== 'delivered' && found.state !== 'cancelled');
      if (!legal) return conflict('That is not a legal transition from the ticket’s current state.');
      const rank = (s: KitchenTicketState): number => ['new', 'accepted', 'preparing', 'ready', 'delivered'].indexOf(s);
      const reached = (s: KitchenTicketState, at: string | null): string | null => (rank(to) < rank(s) ? null : rank(to) === rank(s) ? (at ?? NOW) : at);
      const moved: KitchenTicket = {
        ...found,
        state: to,
        acceptedAt: reached('accepted', found.acceptedAt),
        preparingAt: reached('preparing', found.preparingAt),
        readyAt: reached('ready', found.readyAt),
        deliveredAt: to === 'delivered' ? NOW : null,
        updatedAt: NOW,
      };
      state.tickets = state.tickets.map((one) => (one.id === moved.id ? moved : one));
      return json(200, moved);
    }

    return undefined;
  }

  /** A food order as `getFoodOrder` answers it, built from its tickets; `undefined` when none carries it. */
  function foodOrderOf(orderId: string): FoodOrder | undefined {
    const tickets = state.tickets.filter((one) => one.orderId === orderId);
    const first = tickets[0];
    if (first === undefined) return undefined;
    const inr = (amount: number): Money => ({ amount, currency: 'INR' });
    const lines = tickets.flatMap((one) =>
      one.items.map((item) => ({
        id: item.id.replace('kti_', 'fol_'),
        productId: item.id.replace('kti_', 'prd_'),
        variantId: null,
        name: item.name,
        quantity: item.quantity,
        unitPrice: inr(12000),
        modifiers: [],
        taxRateBps: 0,
        taxable: inr(12000 * item.quantity),
        tax: inr(0),
        total: inr(12000 * item.quantity),
        taxComponents: [],
        preparationArea: one.area,
      })),
    );
    const total = lines.reduce((sum, line) => sum + line.total.amount, 0);
    const cancelled = state.cancelledOrders.has(orderId);
    return {
      id: orderId,
      venueId: VENUE_ID,
      channel: first.stationId === null ? 'counter' : 'kiosk',
      status: cancelled ? 'cancelled' : 'placed',
      settlementStatus: state.settledOrders.has(orderId) ? 'settled' : 'unsettled',
      sessionId: null,
      stationId: first.stationId,
      playerId: null,
      note: first.note,
      lines,
      subtotal: inr(total),
      tax: inr(0),
      total: inr(total),
      routedToKitchen: true,
      placedAt: first.placedAt,
      cancelledAt: cancelled ? NOW : null,
    };
  }

  /** What a sale still owes, for the payments fake's intents; `undefined` for no such sale or one not payable. */
  function outstandingOf(saleId: string): Money | undefined {
    const record = state.sales.find((one) => one.id === saleId);
    if (record === undefined) return undefined;
    const sale = toSale(record);
    return sale.status === 'placed' || sale.status === 'partially_paid' ? sale.outstanding : undefined;
  }

  /**
   * Record an online payment the provider captured (spec/06 §9.1, F-PAY-03) — the payments fake calls
   * this when a test captures an intent, as the server's webhook handler calls the commerce service.
   */
  function recordOnlinePayment(saleId: string, amount: Money, providerPaymentId: string): void {
    const record = state.sales.find((one) => one.id === saleId);
    if (record === undefined) return;
    const payment: SalePayment = { id: fresh('spy'), tender: 'online', amount, tendered: null, change: null, reference: providerPaymentId, status: 'succeeded', createdAt: NOW };
    replaceSale({ ...record, payments: [...record.payments, payment] });
  }

  /**
   * A pass bought at the kiosk "pay at the desk" (F-KIO-14): an unpaid kiosk order for the player on a PC,
   * one `time` line, the newest sale. Returns its id.
   */
  function addKioskPassOrder(input: {
    readonly playerId: string | null;
    readonly stationId: string;
    readonly name: string;
    readonly price: number;
    readonly scanToPayReference?: string;
    /** The venue's change-to-wallet capability on this sale read (F-POS-12); absent reads as not available. */
    readonly changeToWalletAvailable?: boolean;
  }): string {
    const saleId = id('sal', 'k01');
    state.sales.unshift({
      id: saleId,
      channel: 'kiosk',
      ...(input.scanToPayReference === undefined ? {} : { scanToPayReference: input.scanToPayReference }),
      playerId: input.playerId,
      stationId: input.stationId,
      note: `Pass bought at the kiosk: ${input.name}`,
      lines: [priceLine({ tail: 'k11', kind: 'time', name: input.name, unitPrice: input.price, taxRateBps: 0, quantity: 1 })],
      payments: [],
      discounts: [],
      refunds: [],
      placedAt: ago(1),
      cancelledAt: null,
      ...(input.changeToWalletAvailable === undefined ? {} : { changeToWalletAvailable: input.changeToWalletAvailable }),
    });
    return saleId;
  }

  /**
   * The one order a console session places when it ends (ADR-0034): a counter order to pay, its lines the booked blocks
   * and controllers (untaxed play), the console offers as its discount, the newest sale. Returns its id.
   */
  function addConsoleSessionOrder(input: {
    readonly sessionId: string;
    readonly stationId: string;
    readonly playerId: string | null;
    readonly lines: readonly { readonly name: string; readonly unitPrice: number; readonly quantity: number }[];
    readonly discount: number;
  }): string {
    const saleId = fresh('sal');
    state.sales.unshift({
      id: saleId,
      channel: 'counter',
      origin: 'console_session',
      sessionId: input.sessionId,
      playerId: input.playerId,
      stationId: input.stationId,
      note: null,
      lines: input.lines.map((line, index) => priceLine({ tail: `c${index}`, kind: 'time', name: line.name, unitPrice: line.unitPrice, taxRateBps: 0, quantity: line.quantity })),
      payments: [],
      // The console offers' cut, carried as one discount (the real api snapshots it as the order's promotion).
      discounts: input.discount > 0 ? [{ kind: 'manual', code: null, type: 'amount', reason: 'Console offer', amount: inr(input.discount) }] : [],
      refunds: [],
      placedAt: NOW,
      cancelledAt: null,
    });
    return saleId;
  }

  /** A counter sale of Masala fries paid in full online (Cashfree), the newest sale; `refundedOnline` is already sent back. Returns its id. */
  function addOnlinePaidSale(input: { readonly refundedOnline?: number } = {}): string {
    const saleId = id('sal', 'o01');
    const lines = [priceLine({ tail: 'o11', product: productNamed('Masala fries'), quantity: 2 })];
    const total = gross(lines);
    const payment: SalePayment = { id: id('spy', 'o21'), tender: 'online', amount: inr(total), tendered: null, change: null, reference: 'cf_payment_001', status: 'succeeded', createdAt: ago(10) };
    const refunds: SaleRefund[] =
      input.refundedOnline === undefined
        ? []
        : [{ id: id('srf', 'o31'), status: 'succeeded', tender: 'online', amount: inr(input.refundedOnline), reason: 'One portion came out cold.', lines: [], requestedAt: ago(5), settledAt: ago(5) }];
    state.sales.unshift({ id: saleId, channel: 'counter', playerId: null, stationId: null, note: null, lines, payments: [payment], discounts: [], refunds, placedAt: ago(12), cancelledAt: null });
    return saleId;
  }

  return { state, handle, outstandingOf, recordOnlinePayment, addOnlinePaidSale, addKioskPassOrder, addConsoleSessionOrder };
}

export type FakeCommerce = ReturnType<typeof createFakeCommerce>;
